class TootoRocHero extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.stage = this.querySelector('[data-tooto-roc-hero-stage]');
    this.ring = this.querySelector('.tooto-roc-hero__ring');
    this.cards = Array.from(this.querySelectorAll('[data-tooto-roc-card]'));
    this.cardVisuals = this.cards.map((card) => card.querySelector('.tooto-roc-hero__slide') || card);
    this.mobileLayout = window.matchMedia('(max-width: 767px)');
    this.compactLayout = window.matchMedia('(max-width: 1023px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.runId = 0;
    this.isVisible = true;
    this.arcFrame = null;
    this.orbitDuration = 48000;
    this.orbitRadius = 42;
    this.orbitElapsed = 0;
    this.lastOrbitTimestamp = null;

    if (!this.stage) return;

    this.cardAngles = this.cards.map((card) => {
      const rotation = Number.parseFloat(window.getComputedStyle(card).getPropertyValue('--tooto-card-rotation')) || 0;
      return rotation + 270;
    });

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.updateArc = this.updateArc.bind(this);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;
        this.syncPausedState();
        if (this.isVisible) this.requestArcFrame();
      },
      { threshold: 0.05 }
    );
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.requestArcFrame();
    this.play();
  }

  disconnectedCallback() {
    this.runId += 1;
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.arcFrame) cancelAnimationFrame(this.arcFrame);
  }

  handleVisibilityChange() {
    this.syncPausedState();
    if (!document.hidden && this.isVisible) this.requestArcFrame();
  }

  syncPausedState() {
    this.toggleAttribute('data-motion-paused', document.hidden || !this.isVisible);
  }

  requestArcFrame() {
    if (this.arcFrame || document.hidden || !this.isVisible) return;
    this.arcFrame = requestAnimationFrame(this.updateArc);
  }

  updateOrbit(timestamp) {
    const canOrbit = this.stage.classList.contains('is-settled') && !this.reducedMotion.matches;

    if (!canOrbit) {
      this.lastOrbitTimestamp = null;
    } else if (this.lastOrbitTimestamp !== null) {
      const elapsedSinceFrame = Math.min(timestamp - this.lastOrbitTimestamp, 64);
      this.orbitElapsed = (this.orbitElapsed + elapsedSinceFrame) % this.orbitDuration;
    }
    if (canOrbit) this.lastOrbitTimestamp = timestamp;

    const orbitRotation = (this.orbitElapsed / this.orbitDuration) * 360;
    const ringAspectRatio = this.ring
      ? this.ring.clientWidth / Math.max(this.ring.clientHeight, 1)
      : 1;

    return this.cards.map((card, index) => {
      const angle = this.cardAngles[index] + orbitRotation;
      const angleInRadians = (angle * Math.PI) / 180;
      const left = 50 + this.orbitRadius * Math.cos(angleInRadians);
      const top = 50 + this.orbitRadius * Math.sin(angleInRadians);
      const tangentRotation = Math.atan2(
        Math.cos(angleInRadians),
        -ringAspectRatio * Math.sin(angleInRadians)
      ) * (180 / Math.PI);

      if (canOrbit) {
        card.style.left = `${left.toFixed(4)}%`;
        card.style.top = `${top.toFixed(4)}%`;
        card.style.setProperty('--tooto-card-rotation', `${tangentRotation.toFixed(3)}deg`);
      }

      return { card, index, centerY: top };
    });
  }

  updateArc(timestamp) {
    this.arcFrame = null;
    const orbitPositions = this.updateOrbit(timestamp);

    const shouldUpdateLayers =
      this.stage.classList.contains('is-assembling') ||
      this.stage.classList.contains('is-settled') ||
      this.compactLayout.matches;
    const cardPositions = shouldUpdateLayers ? orbitPositions : [];

    if (shouldUpdateLayers) {
      [...cardPositions]
        .sort((first, second) => first.centerY - second.centerY || first.index - second.index)
        .forEach(({ card }, layerIndex) => {
          card.style.zIndex = String(layerIndex + 1);
        });
    } else {
      this.cards.forEach((card) => card.style.removeProperty('z-index'));
    }

    const shouldLimitVisibleArc =
      this.compactLayout.matches &&
      (this.mobileLayout.matches || !this.stage.classList.contains('is-settled'));

    if (!shouldLimitVisibleArc) {
      this.cards.forEach((card) => card.classList.remove('is-outside-visible-arc'));
    } else {
      const visibleCount = this.mobileLayout.matches ? 5 : 7;
      const visibleCards = new Set(
        cardPositions
          .sort((a, b) => a.centerY - b.centerY)
          .slice(0, visibleCount)
          .map(({ card }) => card)
      );

      this.cards.forEach((card) => {
        card.classList.toggle('is-outside-visible-arc', !visibleCards.has(card));
      });
    }

    this.requestArcFrame();
  }

  wait(duration) {
    return new Promise((resolve) => window.setTimeout(resolve, duration));
  }

  imageReady(image) {
    if (image.complete && image.naturalWidth > 0) {
      return typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
    }

    return new Promise((resolve) => {
      image.addEventListener('load', async () => {
        if (typeof image.decode === 'function') {
          try {
            await image.decode();
          } catch (_error) {
            // The load event already confirms that the image can be rendered.
          }
        }
        resolve();
      }, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  async waitForImages() {
    const images = Array.from(this.querySelectorAll('img'));
    images.forEach((image) => { image.loading = 'eager'; });
    await Promise.allSettled(images.map((image) => this.imageReady(image)));
  }

  reset() {
    this.runId += 1;
    this.stage.classList.remove(
      'is-button-ready',
      'is-button-above',
      'is-assembling',
      'is-content-entering',
      'is-content-ready',
      'is-settled'
    );
    this.orbitElapsed = 0;
    this.lastOrbitTimestamp = null;
    this.cards.forEach((card) => {
      card.style.removeProperty('left');
      card.style.removeProperty('top');
      card.style.removeProperty('--tooto-card-rotation');
    });
    void this.stage.offsetWidth;
    return this.runId;
  }

  async play() {
    const currentRun = this.reset();

    if (this.reducedMotion.matches) {
      this.stage.classList.add('is-button-ready', 'is-button-above', 'is-content-ready', 'is-settled');
      return;
    }

    await this.waitForImages();
    if (currentRun !== this.runId) return;

    this.stage.classList.add('is-assembling');
    await this.wait(2050);
    if (currentRun !== this.runId) return;

    this.stage.classList.add('is-settled');
    this.stage.classList.remove('is-assembling');
    this.stage.classList.add('is-button-ready', 'is-button-above', 'is-content-entering');
    await this.wait(1200);
    if (currentRun !== this.runId) return;

    this.stage.classList.add('is-content-ready');
    this.stage.classList.remove('is-content-entering');
  }
}

if (!customElements.get('tooto-roc-hero')) {
  customElements.define('tooto-roc-hero', TootoRocHero);
}
